import { Map, List } from 'immutable';

import { EditorState, ContentBlock, ContentState, genKey, Modifier } from 'draft-js';
import detectIndent from 'detect-indent'
import { Block, Entity } from '../constants';


/*
Returns default block-level metadata for various block type. Empty object otherwise.
*/
export const getDefaultBlockData = (blockType, initialData = {}) => {
    switch (blockType) {
        case Block.TODO: return { checked: false };
        default: return initialData;
    }
};


/*
Get currentBlock in the editorState.
*/
export const getCurrentBlock = (editorState) => {
    const selectionState = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const block = contentState.getBlockForKey(selectionState.getStartKey());
    return block;
};

export const addEmptyBlock = (editorState) => {
    const newBlock = new ContentBlock({
        key: genKey(),
        type: 'unstyled',
        text: '',
    })

    const contentState = editorState.getCurrentContent()
    const newBlockMap = contentState.getBlockMap().set(newBlock.key, newBlock)

    return EditorState.push(
        editorState,
        ContentState
        .createFromBlockArray(newBlockMap.toArray())
        .set('selectionBefore', contentState.getSelectionBefore())
        .set('selectionAfter', contentState.getSelectionAfter())
    )
}

/*
Adds a new block (currently replaces an empty block) at the current cursor position
of the given `newType`.
*/
export const addNewBlock = (editorState, newType = Block.UNSTYLED, initialData = {}) => {
    const selectionState = editorState.getSelection();
    if (!selectionState.isCollapsed()) {
        return editorState;
    }
    
    const contentState = editorState.getCurrentContent();
    const key = selectionState.getStartKey();
    const blockMap = contentState.getBlockMap();
    const currentBlock = getCurrentBlock(editorState);

    if (!currentBlock) {
        return editorState;
    }
    if (currentBlock.getLength() === 0) {
        // if (currentBlock.getType() === newType) {
        //     return editorState;
        // }
        const newBlock = currentBlock.merge({
            type: newType,
            data: getDefaultBlockData(newType, initialData),
        });
        const newContentState = contentState.merge({
            blockMap: blockMap.set(key, newBlock),
            selectionAfter: selectionState,
        });
        return EditorState.push(editorState, newContentState, 'change-block-type');
    }
    return editorState;
};


/*
Changes the block type of the current block.
*/
export const resetBlockWithType = (editorState, newType = Block.UNSTYLED, overrides = {}) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const key = selectionState.getStartKey();
    const blockMap = contentState.getBlockMap();
    const block = blockMap.get(key);
    const newBlock = block.mergeDeep(overrides, {
        type: newType,
        data: getDefaultBlockData(newType),
    });
    const newContentState = contentState.merge({
        blockMap: blockMap.set(key, newBlock),
        selectionAfter: selectionState.merge({
        anchorOffset: 0,
        focusOffset: 0,
        }),
    });
    return EditorState.push(editorState, newContentState, 'change-block-type');
};


/*
Update block-level metadata of the given `block` to the `newData`/
*/
export const updateDataOfBlock = (editorState, block, newData) => {
    const contentState = editorState.getCurrentContent();
    const newBlock = block.merge({
        data: newData,
    });
    const newContentState = contentState.merge({
        blockMap: contentState.getBlockMap().set(block.getKey(), newBlock),
    });
    return EditorState.push(editorState, newContentState, 'change-block-data');
};

// const BEFORE = -1;
// const AFTER = 1;

/*
Used from [react-rte](https://github.com/sstur/react-rte/blob/master/src/lib/insertBlockAfter.js)
by [sstur](https://github.com/sstur)
*/
export const addNewBlockAt = (
        editorState,
        pivotBlockKey,
        newBlockType = Block.UNSTYLED,
        initialData = {}
    ) => {
    const content = editorState.getCurrentContent();
    const blockMap = content.getBlockMap();
    const block = blockMap.get(pivotBlockKey);
    if (!block) {
        throw new Error(`The pivot key - ${pivotBlockKey} is not present in blockMap.`);
    }
    const blocksBefore = blockMap.toSeq().takeUntil((v) => (v === block));
    const blocksAfter = blockMap.toSeq().skipUntil((v) => (v === block)).rest();
    const newBlockKey = genKey();

    const newBlock = new ContentBlock({
        key: newBlockKey,
        type: newBlockType,
        text: '',
        characterList: List(),
        depth: 0,
        data: Map(getDefaultBlockData(newBlockType, initialData)),
});

    const newBlockMap = blocksBefore.concat(
        [[pivotBlockKey, block], [newBlockKey, newBlock]],
        blocksAfter
    ).toOrderedMap();

    const selection = editorState.getSelection();

    const newContent = content.merge({
        blockMap: newBlockMap,
        selectionBefore: selection,
        selectionAfter: selection.merge({
            anchorKey: newBlockKey,
            anchorOffset: 0,
            focusKey: newBlockKey,
            focusOffset: 0,
            isBackward: false,
        }),
    });
    return EditorState.push(editorState, newContent, 'split-block');
};

/**
 * Check whether the cursor is between entity of type LINK
 */
export const isCursorBetweenLink = (editorState) => {
let ret = null;
const selection = editorState.getSelection();
const content = editorState.getCurrentContent();
const currentBlock = getCurrentBlock(editorState);
if (!currentBlock) {
    return ret;
}
let entityKey = null;
let blockKey = null;
if (currentBlock.getType() !== Block.ATOMIC && selection.isCollapsed()) {
    if (currentBlock.getLength() > 0) {
    if (selection.getAnchorOffset() > 0) {
        entityKey = currentBlock.getEntityAt(selection.getAnchorOffset() - 1);
        blockKey = currentBlock.getKey();
        if (entityKey !== null) {
        const entity = content.getEntity(entityKey);
        if (entity.getType() === Entity.LINK) {
            ret = {
            entityKey,
            blockKey,
            url: entity.getData().url,
            };
        }
        }
    }
    }
}
return ret;
}

/*
Returns the `boundingClientRect` of the passed selection.
*/
export const getSelectionRect = (selected) => {
    const _rect = selected.getRangeAt(0).getBoundingClientRect();
    // selected.getRangeAt(0).getBoundingClientRect()
    let rect = _rect && _rect.top ? _rect : selected.getRangeAt(0).getClientRects()[0];
    if (!rect) {
        if (selected.anchorNode && selected.anchorNode.getBoundingClientRect) {
            rect = selected.anchorNode.getBoundingClientRect();
            rect.isEmptyline = true;
        } else {
            return null;
        }
    }
    return rect;
};

/*
Returns the native selection node.
*/
export const getSelection = (root) => {
    let t = null;
    if (root.getSelection) {
        t = root.getSelection();
    } else if (root.document.getSelection) {
        t = root.document.getSelection();
    } else if (root.document.selection) {
        t = root.document.selection.createRange().text;
    }
    return t;
};

/*
Recursively finds the DOM Element of the block where the cursor is currently present.
If not found, returns null.
*/
export const getSelectedBlockNode = (root) => {
    const selection = root.getSelection();
    if (selection.rangeCount === 0) {
        return null;
    }
    let node = selection.getRangeAt(0).startContainer;
    // console.log(node);
    do {
        if (node.getAttribute && node.getAttribute('data-block') === 'true') {
            return node;
        }
        node = node.parentNode;
        // console.log(node);
    } while (node !== null);
    return null;
};

export const getSelectionRef = () => {
    const nativeSelection = getSelection(window);
    if (!nativeSelection.rangeCount) {
        return;
    }
    const selectionBoundary = getSelectionRect(nativeSelection);
    const boundingRect = {
        getBoundingClientRect: () => ({
            top: selectionBoundary.y,
            left: selectionBoundary.x,
            bottom: selectionBoundary.y,
            right: selectionBoundary.x,
            width: 1,
            height: 1,
        }),
        clientWidth: 1,
        clientHeight: 1
    }
    return boundingRect
}

export const getIndentation = (text = '', unindent) => {
    
    const DEFAULT_INDENTATION = '   '
    const result = detectIndent(text)
    let newText = text
    console.log(text, unindent, result.amount)
    if(unindent){
        newText = text.slice(4)
    } else if (!unindent) {
        newText = text + DEFAULT_INDENTATION
    }

    return result.indent || DEFAULT_INDENTATION
}

export const removeLastWord = (editorState, sign) => {
    const contentState = editorState.getCurrentContent()
    let selectionState = editorState.getSelection()
    const text = contentState.getPlainText()

    // Get range to remove
    const end = selectionState.getFocusOffset()
    const lastWord = text.split(' ').slice(-1)[0]
    // const start = end - text.split(' ').slice(-1)[0].length
    const start = sign ? end - (lastWord.length - lastWord.indexOf(sign)) : end - lastWord.length
    selectionState = selectionState.merge({ anchorOffset: start, focusOffset: end })

    // Get new content state
    const newContentState = Modifier.removeRange(contentState, selectionState, 'backward')
    const newEditorState = EditorState.push(editorState, newContentState, start === end ? 'backspace-character' : 'remove-range')
    // console.log({start,end, text: newEditorState.getCurrentContent().getPlainText()})
    
    return newEditorState
}

export const getCurrentWord = (editorState, sign) => {
    const contentState = editorState.getCurrentContent()
    let selectionState = editorState.getSelection()
    const text = contentState.getPlainText()

    // Get range to remove
    const cursor = selectionState.getFocusOffset()
    const wordAfter = text.slice(cursor).split(' ')[0]
    const wordBefore = text.slice(0, cursor).split(' ').slice(-1)[0]
    const currentWord = wordAfter.length > 0 ? wordAfter : wordBefore    
    if(sign){
        return sign + currentWord.split(sign).slice(-1)[0]
    } else {
        return currentWord
    }
}